import { CommonModule } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { PaymentDataService, SubscriptionResponse, SubscriptionStatus, TrialInfoResponse } from '@ffd/api-client';
import { DISABLE_GLOBAL_ERROR_TOASTS, routes } from '@libs/utils';

const MS_PER_DAY = 86_400_000;

@Component({
  selector: 'lib-assistant-trial-badge',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './assistant-trial-badge.component.html',
  styleUrl: './assistant-trial-badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantTrialBadgeComponent {
  public readonly siteId = input.required<string>();
  public readonly variant = input<'pill' | 'caption'>('pill');

  private readonly paymentDataService = inject(PaymentDataService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly trialInfo = signal<TrialInfoResponse | null>(null);
  protected readonly subscription = signal<SubscriptionResponse | null>(null);
  private lastLoadedSiteId: string | undefined;

  protected readonly trialDaysRemaining = computed<number | null>(() => {
    const end = this.trialInfo()?.trialEndDate;

    if (!end) {
      return null;
    }

    return Math.max(0, Math.ceil((new Date(end).getTime() - Date.now()) / MS_PER_DAY));
  });

  protected readonly hasSubscription = computed<boolean>(() => this.subscription()?.status === SubscriptionStatus.ACTIVE);

  constructor() {
    effect(() => {
      const siteId = this.siteId();

      if (!siteId || siteId === this.lastLoadedSiteId) {
        return;
      }
      this.lastLoadedSiteId = siteId;

      // showcase never has a trial or a subscription
      if (siteId === routes.showcaseSiteId) {
        this.trialInfo.set(null);
        this.subscription.set(null);
        return;
      }

      this.loadTrialInfo(siteId);
      this.loadSubscription(siteId);
    });
  }

  private loadTrialInfo(siteId: string): void {
    this.paymentDataService
      .getTrialInfo(siteId, 'body', false, { context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true) })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (info) => this.trialInfo.set(info),
        error: () => this.trialInfo.set(null),
      });
  }

  private loadSubscription(siteId: string): void {
    this.paymentDataService
      .getSubscription(siteId, 'body', false, { context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true) })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (sub) => this.subscription.set(sub),
        error: () => this.subscription.set(null),
      });
  }
}
